import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { VolumeMiniTypeEnum } from 'elements-sdk/src'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { PanelService } from 'panel/services/panel.service'
import { MediaOperationsService } from '../services/mediaOperations.service'
import { NotifyService } from 'common/util/services/notify.service'
import { BaseOperation, MediaOperationContext, MediaOperationInput } from './base'

@Injectable({ providedIn: 'root' })
export class DownloadOriginalOp extends BaseOperation {
  icon = 'fas fa-download'
  showInSelectionToolbar = false
  showInSelectionOverflowMenu = true
  showInContextMenu = true
  showInSharedLinkView = true
  supportsMultiple = true
  supportsBundles = true
  supportsDirs = true
  weight = 80

  constructor (
    private translate: TranslateService,
    private mediaPermissions: MediaPermissionsService,
    private api: APIService,
    private panel: PanelService,
    private notify: NotifyService,
  ) {
    super()
    this.label = translate.instant('Download original')
  }

  isVisibleForDir (dir) {
    return this.mediaPermissions.getPermissionForFile(dir)?.allowOriginalDownload ?? false
  }

  isEnabledForDir (dir) {
    return dir.present
  }

  isVisibleForBundle (bundle) {
    return !this.panel.active && bundle.mainfile && this.mediaPermissions.getPermissionForBundle(bundle)?.allowOriginalDownload
  }

  isEnabledForBundle (bundle) {
    return bundle.asset && bundle.mainfile?.present
  }

  isEnabledFor (input: MediaOperationInput, context: MediaOperationContext): boolean {
    if (context.location?.volume.type === VolumeMiniTypeEnum.Cloud &&
      (input.dirs.length || input.bundles.length > 1)) {
      return false
    }
    return super.isEnabledFor(input, context)
  }

  run ({ dirs, bundles }): Promise<void> {
    // Safari will block a popup window if it's opened asynchronously
    // hence this sync wrapper function
    if (bundles.length === 1 && !dirs.length) {
      window.open(`/api/2/media/files/${bundles[0].mainfile.id}/download`)
      return Promise.resolve()
    } else {
      return this._runMultiple({ dirs, bundles })
    }
  }

  async _runMultiple ({ dirs, bundles }) {
    const files = bundles.length ? await this.api.mediaLibrary.getAllMediaFiles({ bundleIn: bundles.map(x => x.id).join(',') }) : []
    const paths = [...files, ...dirs].map(item => item.fullPath)

    // We are still acting sync on a trusted event
    // open tab now to avoid popup blocker
    const tab = window.open('/download-archive/create')

    if (!tab) {
      this.notify.error(this.translate.instant('You need to allow pop-ups in your browser'))
      return
    }

    try {
      const result = await this.api.main.createArchive({
        createDownloadArchive: { paths },
      })
      tab.location = `/download-archive/${result.id}` as any
    } catch (response) {
      tab.close()
      this.notify.errorFromResponse(this.translate.instant('Could not create download'), response)
    }
  }
}

MediaOperationsService.register(DownloadOriginalOp)
